import React, { useEffect, useState } from "react";
import { message, Tooltip } from "antd";
import { CheckOutlined, CloseOutlined, EditOutlined } from "@ant-design/icons";
import {
  updatePortalStatus,
  updateVisitStageStatus,
} from "../../../apis/patientService";
import { errorMessage } from "../../../utils/notificationUtils";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../store/store";
import { getTimelineDetails } from "../../../models/patientSlice";
import { fetchStepperData } from "../../../models/preAuthSlice";
import { API_URL } from "../../../utils/constants";

interface StatusOption {
  id: number;
  state: string;
}

interface ManualPortalStatusProps {
  // stateData: StatusOption[] | null;
  status: string | null;
  visitId?: number;
  visitStageId?: number | null;
  isUpdateable?: boolean;
}

const ManualPortalStatus: React.FC<ManualPortalStatusProps> = ({
  // stateData,
  status,
  visitId,
  visitStageId,
  isUpdateable,
}) => {
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [selectedStatusValue, setSelectedStatusValue] = useState<any>(null);
  const dispatch = useDispatch<AppDispatch>();
  const { patients } = useSelector((state: RootState) => state);
  const [currentStatus, setCurrentStatus] = useState<string | null>(status);
  const [statusOptions, setStatusOptions] = useState<StatusOption[]>([
    { id: 8, state: "Draft" },
    { id: 9, state: "Pending Review" },
    { id: 10, state: "Pending More Info" },
    { id: 2, state: "Initiated" },
    { id: 3, state: "Denied" },
    { id: 4, state: "Completed" },
    { id: 11, state: "Query" },
    { id: 7, state: "Error" },
  ]);

  useEffect(() => {
    if (status) {
      setCurrentStatus(status);
    }
  }, [status]);

    useEffect(() => {
    if (patients?.fetchBasicDetails?.casesResponse?.id && patients?.fetchBasicDetails?.visitInfoResponse?.id) {
      try {
        const caseId = patients.fetchBasicDetails.casesResponse.id;
        const visitId = patients.fetchBasicDetails.visitInfoResponse.id;
        const patientDetailsChangedSSEUrl = `${API_URL}/api/sse/subscribe/patientDetailsChanged`;
        const eventSource = new EventSource(patientDetailsChangedSSEUrl);

        eventSource.onmessage = (event) => {
          try {
            const data = JSON.parse(event?.data);
            if (data?.actionType === "patientDetailsChanged" && data?.portalStatus && caseId === data?.caseId && visitId === data?.visitId) {
              setCurrentStatus(data?.portalStatus);
            }
          } catch {
            console.error("Error parsing SSE data", event?.data);
          }
        };

        eventSource.onerror = () => {
          eventSource.close();
        };

        return () => {
          eventSource.close();
        };
        } catch (error) {
          console.error("Error setting up claimIdChanged SSE subscription:", error);
        }
      }
    }, [
    patients?.fetchBasicDetails?.casesResponse?.id,
    patients?.fetchBasicDetails?.visitInfoResponse?.id
  ]);

  const handleEditClick = () => {
    setIsEditing(true);
    setSelectedStatusValue(selectedStatusValue);
  };

  const save = async (value: string) => {
    try {
      if (visitId) {
        const res = await updatePortalStatus(visitId, value);

        if (res) {
          message.success(`Portal Status Updated Successfully`);
          if (
            patients?.fetchBasicDetails?.casesResponse?.id &&
            patients?.fetchBasicDetails?.visitInfoResponse?.id
          ) {
            dispatch(
              getTimelineDetails({
                caseId: patients?.fetchBasicDetails?.casesResponse?.id,
                visitId: patients?.fetchBasicDetails?.visitInfoResponse?.id,
              })
            );
            dispatch(
              fetchStepperData({
                caseId: patients?.fetchBasicDetails?.casesResponse?.id,
                visitId: patients?.fetchBasicDetails?.visitInfoResponse?.id,
              })
            );
          }
        }
      }
    } catch (error: any) {
      let errorMessage = "An unexpected error occurred";

      if (error?.response?.data) {
        const backendMessage = error.response.data;

        errorMessage = backendMessage;
      } else if (error?.message) {
        errorMessage = error.message;
      }

      message.error(errorMessage);
      if (status) {
        setCurrentStatus(status);
      }
    }
  };

  const handleCancelClick = () => {
    setIsEditing(false);
    setSelectedStatusValue(null);
  };

  const handleSaveClick = () => {
    if (selectedStatusValue) {
      setCurrentStatus(selectedStatusValue);
      save(selectedStatusValue);
    }
    setIsEditing(false);
  };

  const handleChange = (value: string) => {
    setSelectedStatusValue(value);
  };

  // SSE subscription for portalStatusChanged events
  useEffect(() => {
    if (patients?.fetchBasicDetails?.casesResponse?.id && patients?.fetchBasicDetails?.visitInfoResponse?.id) {
      try {
        const caseId = patients.fetchBasicDetails.casesResponse.id;
        const visitId = patients.fetchBasicDetails.visitInfoResponse.id;
        const patientDetailsChangedSSEUrl = `${API_URL}/api/sse/subscribe/patientDetailsChanged`;
        const eventSource = new EventSource(patientDetailsChangedSSEUrl);
    
        eventSource.onmessage = (event) => {
          try {
            const data = JSON.parse(event?.data);
            if (data?.actionType === "patientDetailsChanged" && data?.portalStatus && caseId === data?.caseId && visitId === data?.visitId) {
              setCurrentStatus(data?.portalStatus);
            }
          } catch {
            console.error("Error parsing SSE data", event?.data);
          }
        };
            
        eventSource.onerror = () => {
          eventSource.close();
        };
    
        return () => {
          eventSource.close();
        };
        } catch (error) {
          console.error("Error setting up claimIdChanged SSE subscription:", error);
        }
      }
    }, [
    patients?.fetchBasicDetails?.casesResponse?.id,
    patients?.fetchBasicDetails?.visitInfoResponse?.id
  ]);

  return (
    <>
      <div
        className="status-box"
        style={{ background: "#F2F6F9", color: "black" }}
      >
        <div className="status-content">
          <span
            style={{ fontWeight: 400, color: "#575757", fontSize: "14px" }}
            className="status-label"
          >
            Portal Status:
          </span>
          <div style={{ display: "flex", alignItems: "center"}}>
            {isEditing ? (
              // <select
              //   className="status-value"
              //   value={selectedStatusValue?.id || ""}
              //   onChange={(e) => handleChange(e.target.value)}
              // >
              //   <option
              //     value=""
              //     disabled
              //     style={{ textTransform: "uppercase" }}
              //   >
              //     -- Select a Status --
              //   </option>
              //   {statusOptions.map((option) => (
              //     <option key={option.id} value={option.id}>
              //       {option.state}
              //     </option>
              //   ))}
              // </select>
              <input
                type="text"
                name="portalStatus"
                defaultValue={currentStatus || ""}
                onChange={(e) => handleChange(e.target.value)}
              />
            ) : (
              // <span style={{ fontWeight: 600 }} className="claim-id-value">
              //   {currentStatus || "-"}
              // </span>
              <>
                {currentStatus ? (
                  <Tooltip title={currentStatus.toUpperCase()}>
                    <span
                      className="claim-id-value"
                      style={{
                        // color: "darkcyan",
                        fontWeight: 600,
                        fontSize: "14px",
                      }}
                    >
                      {currentStatus.toUpperCase().length > 20
                        ? `${currentStatus.toUpperCase().substring(0, 15)}...` +
                          `${currentStatus.toUpperCase().slice(-6)}`
                        : currentStatus.toUpperCase()}
                    </span>
                  </Tooltip>
                ) : (
                  "-"
                )}
              </>
            )}
            {!isEditing && isUpdateable ? (
              // && !currentStatus?.toLocaleUpperCase()?.includes("COMPLETED")
              <Tooltip title="Edit Status">
                <EditOutlined
                  style={{
                    marginLeft: "8px",
                    cursor: "pointer",
                    fontSize: "14px",
                  }}
                  onClick={handleEditClick}
                />
              </Tooltip>
            ) : (
              isUpdateable && (
                <>
                  <Tooltip title="Cancel">
                    <CloseOutlined
                      style={{
                        marginLeft: "8px",
                        cursor: "pointer",
                        fontSize: "14px",
                      }}
                      onClick={handleCancelClick}
                    />
                  </Tooltip>
                  <Tooltip title="Save">
                    <CheckOutlined
                      style={{
                        marginLeft: "8px",
                        cursor: "pointer",
                        fontSize: "14px",
                      }}
                      onClick={handleSaveClick}
                    />
                  </Tooltip>
                </>
              )
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default ManualPortalStatus;
